import {
    Box,
    Button,
    Container,
    Field,
    Flex,
    Image,
    Input,
    InputGroup,
    Link,
    Stack,
    Text,
    useBreakpointValue,
    VStack,
} from "@chakra-ui/react";
import Head from "next/head";
import React, { useState } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { usePostFormMutation } from "@/hooks/microservicesApi";
import Header from "@/sections/home/Header";
import { emailRegex } from "@/utilities/text/regex";

const ContactLink = ({ children, icon, href }) => {
    return (
        <Link fontSize="md" mb={2} href={href}>
            <Stack direction="row">
                <Image boxSize="20px" objectFit="cover" src={icon} alt={children} loading="lazy" />
                <Text>{children}</Text>
            </Stack>
        </Link>
    );
};

export default function Contacto() {
    const isGreaterThanMd = useBreakpointValue({ base: false, md: true });
    const [nombre, setNombre] = useState("");
    const [apellidos, setApellidos] = useState("");
    const [telefono, setTelefono] = useState("");
    const [email, setEmail] = useState("");
    const [nombreError, setNombreError] = useState(false);
    const [apellidoError, setApellidoError] = useState(false);
    const [telefonoError, setTelefonoError] = useState("");
    const [emailError, setEmailError] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [postForm] = usePostFormMutation();

    const icons = [
        { imgUrl: "https://media.grupoguia.mx/assets/home-page/logos/logo-guia-transparent.webp" },
        { imgUrl: "https://media.grupoguia.mx/assets/home-page/icons/mexico.svg" },
        { imgUrl: "https://media.grupoguia.mx/assets/home-page/footer/llamada-telefonica.webp" },
        { imgUrl: "https://media.grupoguia.mx/assets/home-page/footer/correo-electronico.webp" },
    ];

    const handleSubmit = () => {
        const body = {
            id: "5ccf4867-7379-4c52-9282-9b39cf885c7a",
            body: {
                fields: [
                    {
                        name: "firstname",
                        value: nombre,
                    },
                    {
                        name: "lastname",
                        value: apellidos,
                    },
                    {
                        name: "email",
                        value: email,
                    },
                    {
                        name: "phone",
                        value: telefono,
                    },
                ],
                context: {
                    ipAddress: "3.14.97.137",
                    pageUri: "www.grupoguia.mx/contacto",
                    pageName: "Grupo Guia | Contacto",
                },
            },
        };
        postForm(body)
            .then((response) => {
                setShowInfo(true);
            })
            .catch((error) => {
                console.error(error);
            });
    };

    return (
        <>
            <Head>
                <title>Contacto | Grupo Guía</title>
                <link rel="canonical" href="https://grupoguia.mx/contacto" />
                <link rel="alternate" href="https://grupoguia.mx/contacto" hrefLang="es-mx" />
            </Head>
            <Header />
            <Box pt={5} pb={20} minHeight="100vh" bg="#E5E5E5" mt={16}>
                <Container
                    maxW={{ base: "md", md: "6xl" }}
                    padding={{ base: 5, md: 18 }}
                    mx="auto"
                    color="black"
                >
                    <Stack textAlign="center" alignItems="center">
                        <Image src={icons[0].imgUrl} w={24} mb={4} alt="simple icon" />
                        <Text fontSize="xl" fontWeight={400}>
                            ¡Completa tus datos
                            <br />
                            <Text fontSize="xl" fontWeight={600}>
                                y encuentra el lugar para ti!
                            </Text>
                        </Text>
                    </Stack>
                    <VStack
                        borderRadius="lg"
                        margin="auto"
                        mt={8}
                        maxW={isGreaterThanMd ? "40%" : "100%"}
                        boxShadow="lg"
                        bg="white"
                        py={12}
                    >
                        {!showInfo ? (
                            <form
                                style={{ width: "80%" }}
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSubmit();
                                }}
                            >
                                <Field.Root mb={3} invalid={nombreError} gap={0} required>
                                    <Input
                                        onInvalid={() => setNombreError(true)}
                                        rounded="full"
                                        placeholder="Nombre(s)"
                                        marginBottom="10px"
                                        id="nombres"
                                        type="text"
                                        py={5}
                                        value={nombre}
                                        onChange={(e) => {
                                            e.preventDefault();
                                            setNombre(e.target.value);
                                        }}
                                        onBlur={(e) => {
                                            e.preventDefault();
                                            setNombre(e.target.value);
                                            if (e.target.value.trim() !== "") {
                                                setNombreError(false);
                                            } else {
                                                setNombreError(true);
                                            }
                                        }}
                                        px={4}
                                    />
                                    <Field.ErrorText ml="4" color="red.500" mb={4}>
                                        El nombre es necesario
                                    </Field.ErrorText>
                                </Field.Root>
                                <Field.Root mb={3} invalid={apellidoError}>
                                    <Input
                                        onInvalid={() => setApellidoError(true)}
                                        rounded="full"
                                        placeholder="Apellidos"
                                        marginBottom="10px"
                                        value={apellidos}
                                        id="apellidos"
                                        type="text"
                                        py={5}
                                        required
                                        onChange={(e) => {
                                            e.preventDefault();
                                            setApellidos(e.target.value);
                                        }}
                                        onBlur={(e) => {
                                            e.preventDefault();
                                            setApellidos(e.target.value);
                                            if (e.target.value.trim() !== "") {
                                                setApellidoError(false);
                                            } else {
                                                setApellidoError(true);
                                            }
                                        }}
                                        px={4}
                                    />
                                    <Field.ErrorText ml="4" color="red.500" mb={4}>
                                        El apellido es necesario
                                    </Field.ErrorText>
                                </Field.Root>
                                <Field.Root mb={3} invalid={telefonoError} required gap={0}>
                                    <InputGroup
                                        marginBottom="20px"
                                        borderLeftRadius="full"
                                        overflow="hidden"
                                        startAddon={
                                            <Flex
                                                gap={2}
                                                p={2}
                                                w="100%"
                                                h="100%"
                                                bg="#FFF"
                                                align="center"
                                                borderLeftRadius="full"
                                            >
                                                <Box backgroundColor="#FFFFFF" rounded="full">
                                                    <Image
                                                        src="https://media.grupoguia.mx/assets/home-page/icons/mexico.svg"
                                                        width="16px"
                                                        height="16px"
                                                        alt="mexico"
                                                    />
                                                </Box>
                                                <Box
                                                    backgroundColor="#FFFFFF"
                                                    borderLeftColor="#FFFFFF"
                                                    borderRadius="0px"
                                                    padding="0px"
                                                    paddingRight="10px"
                                                    color="#000"
                                                >
                                                    +52
                                                </Box>
                                            </Flex>
                                        }
                                    >
                                        <Input
                                            onInvalid={() => setTelefonoError(true)}
                                            borderRadius="24px"
                                            placeholder="Teléfono"
                                            type="number"
                                            id="telefono"
                                            py={5}
                                            value={telefono}
                                            required
                                            onChange={(e) => {
                                                e.preventDefault();
                                                setTelefono(e.target.value);
                                            }}
                                            onBlur={(e) => {
                                                e.preventDefault();
                                                setTelefono(e.target.value);
                                                if (
                                                    e.target.value.trim() !== "" &&
                                                    e.target.value.length >= 10
                                                ) {
                                                    setTelefonoError(false);
                                                } else {
                                                    setTelefonoError(true);
                                                }
                                            }}
                                            px={4}
                                        />
                                    </InputGroup>
                                    <Field.ErrorText ml="4" color="red.500" mb={4}>
                                        El teléfono no es válido
                                    </Field.ErrorText>
                                </Field.Root>
                                <Field.Root mb={3} invalid={emailError}>
                                    <Input
                                        onInvalid={() => setEmailError(true)}
                                        rounded="full"
                                        placeholder="Correo electrónico"
                                        marginBottom="10px"
                                        value={email}
                                        id="email"
                                        type="email"
                                        py={5}
                                        required
                                        onChange={(e) => {
                                            e.preventDefault();
                                            setEmail(e.target.value);
                                        }}
                                        onBlur={(e) => {
                                            e.preventDefault();
                                            setEmail(e.target.value);
                                            if (
                                                e.target.value.trim() != "" &&
                                                emailRegex.test(e.target.value)
                                            ) {
                                                setEmailError(false);
                                            } else {
                                                setEmailError(true);
                                            }
                                        }}
                                        px={4}
                                    />
                                    <Field.ErrorText ml="4" color="red.500" mb={4}>
                                        La dirección de correo no es válida
                                    </Field.ErrorText>
                                </Field.Root>

                                <Button
                                    rounded="full"
                                    w="100%"
                                    fontWeight="500"
                                    color="black"
                                    borderColor="#FFCD00"
                                    _hover={{
                                        bg: "#EDBE00",
                                    }}
                                    type="submit"
                                    bg="brand.500"
                                >
                                    Enviar
                                </Button>
                                <VStack alignItems="center" mt="4">
                                    <Checkbox
                                        required
                                        size="sm"
                                        colorPalette="yellow"
                                        color="accent.500"
                                    >
                                        Acepto los{" "}
                                        <Link
                                            textDecoration="underline"
                                            color="blue.600"
                                            href="/terminosycondiciones"
                                            external
                                        >
                                            {" "}
                                            Términos y condiciones.{" "}
                                        </Link>
                                    </Checkbox>
                                </VStack>
                            </form>
                        ) : (
                            <>
                                <Text fontSize="xl" fontWeight="700" color="accent.500">
                                    {" "}
                                    ¡Gracias!{" "}
                                </Text>
                                <Text pb={4}>¡Llámanos y contáctate con un asesor!</Text>
                                <ContactLink href="tel:+5213336426564" icon={icons[2].imgUrl}>
                                    (33) 3642 6564
                                </ContactLink>
                                <ContactLink
                                    href="mailto:contacto@grupoguia.mx"
                                    icon={icons[3].imgUrl}
                                >
                                    contacto@grupoguia.mx
                                </ContactLink>
                            </>
                        )}
                    </VStack>
                </Container>
            </Box>
        </>
    );
}
